import { useState, useEffect, useCallback } from "react";
import { Film, Check, ExternalLink, CloudUpload, Video, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function openOAuthPopup(url, onSuccess) {
  const w = 600, h = 700;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  const popup = window.open(url, "gemvault_oauth", `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`);

  const listener = (event) => {
    if (event.data?.type === "oauth_callback") {
      window.removeEventListener("message", listener);
      onSuccess(event.data);
    }
  };
  window.addEventListener("message", listener);

  // Fallback: poll for popup close (in case message fails)
  const poll = setInterval(() => {
    if (popup && popup.closed) {
      clearInterval(poll);
      window.removeEventListener("message", listener);
      // Refresh user state regardless
      onSuccess({ type: "oauth_callback", status: "check" });
    }
  }, 500);
}

export default function ConnectServices({ user, onUserUpdated, onContinue }) {
  const [connecting, setConnecting] = useState("");
  const [localUser, setLocalUser] = useState(user);

  const refreshUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me`);
      if (res.data.user) {
        setLocalUser(res.data.user);
        if (onUserUpdated) onUserUpdated(res.data.user);
      }
    } catch { /* silent */ }
  }, [onUserUpdated]);

  const handleConnect = async (service) => {
    setConnecting(service);
    try {
      const res = await axios.get(`${API}/auth/${service}`);
      if (res.data.url) {
        openOAuthPopup(res.data.url, async (data) => {
          await refreshUser();
          setConnecting("");
          if (data.status === "connected") {
            toast.success(`${service === "vimeo" ? "Vimeo" : "Dropbox"} connected`);
          }
        });
      } else {
        toast.error(res.data.error || `${service} OAuth not configured`);
        setConnecting("");
      }
    } catch {
      toast.error(`Failed to start ${service} connection`);
      setConnecting("");
    }
  };

  const vimeoConnected = !!localUser?.vimeo_token;
  const dropboxConnected = !!localUser?.dropbox_token;
  const gdriveConnected = !!localUser?.gdrive_token;
  const hasDestination = dropboxConnected || gdriveConnected;
  const canProceed = vimeoConnected && hasDestination;

  return (
    <div className="min-h-screen bg-[#0f1114] flex items-center justify-center p-6 relative z-10">
      <div className="max-w-lg w-full">
        {/* Brand */}
        <div className="text-center mb-12">
          <div className="section-label mb-4">// connect your accounts</div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#f4f4f5] tracking-tight mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Almost There
          </h1>
          <p className="text-sm text-[#a1a1aa]">
            Connect your Vimeo and choose where to send your footage.
          </p>
        </div>

        {/* Step 1: Vimeo */}
        <div className={`ug-card p-6 mb-4 transition-all ${vimeoConnected ? "!border-[#5fa87a]/40" : ""}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${vimeoConnected ? "bg-[#5fa87a]/10" : "bg-[#21252b]"}`}>
                {vimeoConnected ? <Check className="h-5 w-5 text-[#5fa87a]" /> : <Video className="h-5 w-5 text-[#71717a]" />}
              </div>
              <div>
                <h3 className="text-sm font-medium text-[#e4e4e7]">Vimeo</h3>
                <p className="text-[10px] text-[#71717a] font-mono tracking-wider">
                  {vimeoConnected ? `// connected as ${localUser?.vimeo_user || "your account"}` : "// where your footage lives"}
                </p>
              </div>
            </div>
            {!vimeoConnected && (
              <button
                data-testid="connect-vimeo-btn"
                onClick={() => handleConnect("vimeo")}
                disabled={connecting === "vimeo"}
                className="btn-gold px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                {connecting === "vimeo" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
                {connecting === "vimeo" ? "Connecting..." : "Connect"}
              </button>
            )}
          </div>
        </div>

        {/* Step 2: Dropbox */}
        <div className={`ug-card p-6 mb-4 transition-all ${dropboxConnected ? "!border-[#5fa87a]/40" : ""}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dropboxConnected ? "bg-[#5fa87a]/10" : "bg-[#21252b]"}`}>
                {dropboxConnected ? <Check className="h-5 w-5 text-[#5fa87a]" /> : <CloudUpload className="h-5 w-5 text-[#71717a]" />}
              </div>
              <div>
                <h3 className="text-sm font-medium text-[#e4e4e7]">Dropbox</h3>
                <p className="text-[10px] text-[#71717a] font-mono tracking-wider">
                  {dropboxConnected ? `// connected as ${localUser?.dropbox_user || "your account"}` : "// where your footage goes"}
                </p>
              </div>
            </div>
            {!dropboxConnected && (
              <button
                data-testid="connect-dropbox-btn"
                onClick={() => handleConnect("dropbox")}
                disabled={connecting === "dropbox"}
                className="btn-gold px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                {connecting === "dropbox" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
                {connecting === "dropbox" ? "Connecting..." : "Connect"}
              </button>
            )}
          </div>
        </div>

        {/* Google Drive */}
        <div className={`ug-card p-6 mb-8 transition-all ${gdriveConnected ? "!border-[#5fa87a]/40" : ""}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${gdriveConnected ? "bg-[#5fa87a]/10" : "bg-[#21252b]"}`}>
                {gdriveConnected ? <Check className="h-5 w-5 text-[#5fa87a]" /> : <CloudUpload className="h-5 w-5 text-[#71717a]" />}
              </div>
              <div>
                <h3 className="text-sm font-medium text-[#e4e4e7]">Google Drive</h3>
                <p className="text-[10px] text-[#71717a] font-mono tracking-wider">
                  {gdriveConnected ? `// connected as ${localUser?.gdrive_user || "your account"}` : "// alternative cloud destination"}
                </p>
              </div>
            </div>
            {!gdriveConnected && (
              <button
                data-testid="connect-gdrive-btn"
                onClick={() => handleConnect("gdrive")}
                disabled={connecting === "gdrive"}
                className="btn-ghost px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 disabled:opacity-50 !border-[#363b45] !text-[#a1a1aa]"
              >
                {connecting === "gdrive" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
                {connecting === "gdrive" ? "Connecting..." : "Connect"}
              </button>
            )}
          </div>
        </div>

        {/* Continue */}
        {canProceed && (
          <button
            data-testid="continue-to-dashboard-btn"
            onClick={onContinue}
            className="btn-gold w-full h-12 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
          >
            Start Transferring
          </button>
        )}

        {!canProceed && !connecting && (
          <p className="text-xs text-[#52525b] text-center font-mono tracking-wider">
            // connect vimeo + at least one destination to get started
          </p>
        )}

        {connecting && (
          <p className="text-xs text-[#c6a96f] text-center font-mono tracking-wider">
            // authorize in the popup window — you'll stay right here
          </p>
        )}
      </div>
    </div>
  );
}
